// Notifications — what the software noticed (round 37).
//
// The other half of the split Nathan asked for. Announcements is the human
// feed: somebody writes a post, a crew acknowledges it. This one is written by
// nobody — expiring registrations, services due, tickets about to lapse,
// documents running out, and the two dates worth knowing about a person.
// Zanetto Tools carries the same pair and settled the naming the same way.
//
// NOTHING IS STORED behind it. The server computes the list on read from the
// same engine Home's summary uses (`lib/reminderData.ts`), so renewing a rego
// clears its row immediately and the two screens cannot disagree.
//
// Grouped by URGENCY, not by kind — ZT's call and the right one: what a
// manager needs first is what is already a problem, not whether it happens to
// be a rego or a birthday. The icon and the chip say which it is.
//
// The rows themselves live in components/ReminderList.tsx, shared with Home's
// summary (round 38) so the two screens cannot drift.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api';
import {
  notificationsFeed,
  type NotificationsFeed,
  type Reminder,
  type ReminderKind,
} from '../homeApi';
import { Banner } from '../ui';
import { PageHeader } from '../components/ui';
import {
  REMINDER_KIND_LABEL,
  REMINDER_KINDS,
  ReminderGroups,
} from '../components/ReminderList';

export function NotificationsPage() {
  // Every chip renders, at zero too (the strip's rule). The Quotes chip was
  // only for companies with quotes on until round 45 made them always on.
  const kinds = REMINDER_KINDS;
  const [data, setData] = useState<NotificationsFeed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<ReminderKind | 'all'>('all');

  const load = useCallback(async () => {
    try {
      setData(await notificationsFeed.load());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your notifications.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => {
    const empty = { overdue: [], today: [], upcoming: [] };
    if (!data) return empty;
    if (kind === 'all') return data.groups;
    const keep = (items: Reminder[]) => items.filter((i) => i.kind === kind);
    return {
      overdue: keep(data.groups.overdue),
      today: keep(data.groups.today),
      upcoming: keep(data.groups.upcoming),
    };
  }, [data, kind]);

  const nothing =
    groups.overdue.length === 0 && groups.today.length === 0 && groups.upcoming.length === 0;

  /** The server sends every key; this is the type saying so. */
  const count = (key: string): number => data?.counts[key] ?? 0;

  // The WIDER of the two windows, or the heading contradicts the rows under
  // it: a licence expiring in six weeks appears in that group, and "Next 7
  // days" over it would be the page arguing with itself (round 27's rule).
  const horizon = Math.max(data?.leadDays ?? 7, data?.personLeadDays ?? 60);

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle={
          count('badge') > 0
            ? `${count('badge')} ${count('badge') === 1 ? 'thing needs' : 'things need'} attention today.`
            : 'Registrations, services, tickets, insurances, documents, birthdays and work anniversaries.'
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      {/* EVERY kind, even at zero — a chip that appears only when it has
          something in it makes the strip change shape as the week goes on,
          and a reader who filtered to Tickets yesterday finds the filter
          gone today rather than empty, which reads as the feature having
          broken. ZT shows the whole set; so does this. The one exception is
          the WHOLE strip (round 38's review): with nothing behind ANY of
          them there is nothing to filter, and seven zeros over the empty
          state read as seven separate things being wrong on a brand-new
          company. The chips return, all of them, the first time anything
          exists. */}
      {data && count('total') > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          <Chip
            label="All"
            count={count('total')}
            on={kind === 'all'}
            onPick={() => setKind('all')}
          />
          {kinds.map((k) => (
            <Chip
              key={k}
              label={REMINDER_KIND_LABEL[k]}
              count={count(k)}
              on={kind === k}
              onPick={() => setKind(k)}
            />
          ))}
        </div>
      )}

      {data === null ? (
        <p className="muted">Loading…</p>
      ) : nothing ? (
        // A dashed outline rather than a card: an empty state is the SHAPE of
        // where something will be, and drawing it as a solid card says a thing
        // is there.
        <div className="rounded-lg border border-dashed border-gray-300 px-5 py-16 text-center">
          <p className="!mb-1 text-sm font-medium text-gray-600">
            {kind === 'all' ? 'Nothing needs attention' : `No ${REMINDER_KIND_LABEL[kind].toLowerCase()} coming up`}
          </p>
          <p className="!mb-0 text-xs text-gray-500">
            {kind === 'all'
              ? 'Registrations, services, tickets, insurances and documents all have room to run. Anything coming up will appear here.'
              : 'Try another filter, or All.'}
          </p>
        </div>
      ) : (
        <ReminderGroups groups={groups} horizon={horizon} />
      )}
    </>
  );
}

function Chip({
  label,
  count,
  on,
  onPick,
}: {
  label: string;
  count: number;
  on: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={on}
      className={`min-h-[44px] rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 sm:min-h-[32px] ${
        on ? 'bg-brand-500 text-ink' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
      <span className="ml-1.5 opacity-70">{count}</span>
    </button>
  );
}

